'use client'
import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'next/navigation'
import TextField from '@mui/material/TextField'
import { RegularSection, ColorTitle } from '@/components/Layouts'
import { Box, List, ListItem, ListItemButton, Typography } from '@mui/material'

interface Match {
    slug: string
    text: string
    title?: string
    collection?: string
}

interface Content {
    title?: string
}

interface SearchProps {
    content?: Content
    urlParams?: {
        q?: string
    }
    blockName?:string
}

const Index = (props: SearchProps) => {
    const { content = {}, urlParams } = props
    const { title } = content
    const searchParams = useSearchParams()
    const queryFromUrl = useMemo(() => {
        const value = searchParams?.get('q')
        if (typeof value === 'string' && value.length > 0) {
            return value
        }
        if (typeof urlParams?.q === 'string') {
            return urlParams.q
        }
        return ''
    }, [searchParams, urlParams?.q])

    const [searchText, setSearchText] = useState(queryFromUrl)
    const [matches, setMatches] = useState<Match[]>([])
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        if (queryFromUrl !== searchText) {
            setSearchText(queryFromUrl)
        }
    }, [queryFromUrl, searchText])

    useEffect(() => {
        let cancelled = false

        async function runSearch() {
            if (!searchText) {
                setMatches([])
                return
            }

            setIsLoading(true)

            try {
                const params = new URLSearchParams({ q: searchText })
                const res = await fetch(`/api/search?${params.toString()}`)
                if (!res.ok) {
                    console.error('Search request failed', res.status)
                    return
                }
                const payload = await res.json()
                if (!cancelled) {
                    const rawResults = Array.isArray(payload?.results) ? (payload.results as unknown[]) : []
                    const filteredResults = rawResults.filter((item): item is Match => {
                        if (!item || typeof item !== 'object') {
                            return false
                        }
                        const candidate = item as Partial<Match>
                        return typeof candidate.slug === 'string' && typeof candidate.text === 'string'
                    })
                    setMatches(filteredResults)
                }
            } catch (error) {
                console.error('Search request failed', error)
            } finally {
                if (!cancelled) {
                    setIsLoading(false)
                }
            }
        }

        runSearch()

        return () => {
            cancelled = true
        }
    }, [searchText])


    return (
        <RegularSection aria-label='Search' >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row'},
                    gap: 2,
                    width: '100%',
                }}
            >
                <ColorTitle title={title} colorFrom={0} textAlign='start'/>
                <Typography variant='caption' sx={{ width: 150}}>
                    {isLoading ? 'Searching…' : `Found: ${matches.length}`}
                </Typography>
                
                <TextField
                    id='outlined-search'
                    label='Type to search...'
                    type='search'
                    variant='outlined'
                    size='small'
                    value={searchText}
                    sx={{ width: '100%' }}
                    onChange={(e) => setSearchText(e.target.value)}
                />
            </Box>



            <List sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                {matches.map((match) => (
                    <ListItem key={match.slug}>
                        <ListItemButton
                            component='a'
                            href={match.slug}
                            sx={{ alignItems: 'flex-start', flexDirection: 'column', gap: 0.5 }}
                        >
                            {match.title && (
                                <Typography variant='subtitle1' fontWeight='bold'>
                                    {match.title}
                                </Typography>
                            )}
                            {match.collection && (
                                <Typography variant='caption' color='text.secondary'>
                                    {match.collection}
                                </Typography>
                            )}
                            <Typography variant='body2' sx={{ whiteSpace: 'pre-line' }}>
                                {match.text.split(/(\[[^\]]+\])/g).map((part, idx) => {
                                    const matchBrackets = part.match(/^\[(.*)\]$/)
                                    if (matchBrackets) {
                                        return (
                                            <span
                                                key={idx}
                                                style={{ backgroundColor: 'yellow', fontWeight: 500 }}
                                            >{` ${matchBrackets[1]} `}</span>
                                        )
                                    }
                                    return <span key={idx}>{part}</span>
                                })}
                            </Typography>
                        </ListItemButton>
                    </ListItem>
                ))}
                {!isLoading && searchText && matches.length === 0 && (
                    <ListItem>
                        <Typography variant='body2' color='text.secondary'>
                            No results yet. Try adjusting your search terms.
                        </Typography>
                    </ListItem>
                )}
            </List>
        </RegularSection>
    )
}

export default Index
